import { ApiDelete, ApiGet, ApiPost, ApiPostStream, ApiPut } from './Common.ts'
import { GetOrderEntryConnectorUrl } from '@/stores/SettingsStore.ts'

/**
 * Retrieves a list of RMAs based on filter criteria
 * @param filter The filter containing DatabaseFilter for RMA filtering
 * @returns Promise with the array of RMA master view models
 */
export async function RmaMasterGetList(filter: unknown){
  console.log('filter', filter)
  const url = await GetOrderEntryConnectorUrl('RmaMasterGetList')
  // Pass the filter directly to match backend expectations
  return ApiGet(url, filter)
}

/**
 * Retrieves a specific RMA by its number
 * @param rmaNumber The RMA number to retrieve
 * @returns Promise with the RMA master view model
 */
export async function RmaMasterGetByNumber(rmaNumber: string) {
  const url = await GetOrderEntryConnectorUrl('RMAGet')
  return ApiPost(url, { RMANumber: rmaNumber })
}

/**
 * Creates a new RMA
 * @param aoeOrderId The order ID for the RMA
 * @param addressId Optional address ID to override the default return address
 * @returns Promise with the RMA creation response including RMA number
 */
export async function RmaCreate(aoeOrderId: number, addressId?: number) {
  const url = await GetOrderEntryConnectorUrl('RmaCreate')
  const payload: { AOEOrderId: number; AddressId?: number } = { AOEOrderId: aoeOrderId }
  if (addressId) {
    payload.AddressId = addressId
  }
  return ApiPost(url, payload)
}

/**
 * Creates an inbound SKU for an RMA
 * @param rmaNumber The RMA number
 * @param skuCode The SKU code
 * @param quantity The quantity
 * @param orderLineNumber The order line number
 * @param reasonSystemKeyword The reason system keyword (e.g., 'Damaged')
 * @returns Promise with the response
 */
export async function RmaInboundSkuCreate(
  rmaNumber: string,
  skuCode: string,
  quantity: number,
  orderLineNumber: number,
  reasonSystemKeyword: string
) {
  const url = await GetOrderEntryConnectorUrl('RmaInboundSkuCreate')
  return ApiPost(url, {
    RMANumber: rmaNumber,
    SkuCode: skuCode,
    Quantity: quantity,
    OrderLineNumber: orderLineNumber,
    ReasonSystemKeyword: reasonSystemKeyword
  })
}

/**
 * Creates an outbound SKU for an RMA
 * @param rmaNumber The RMA number
 * @param skuCode The SKU code
 * @param quantity The quantity
 * @returns Promise with the response
 */
export async function RmaOutboundSkuCreate(
  rmaNumber: string,
  skuCode: string,
  quantity: number
) {
  const url = await GetOrderEntryConnectorUrl('RmaOutboundSkuCreate')
  return ApiPost(url, {
    RMANumber: rmaNumber,
    SkuCode: skuCode,
    Quantity: quantity
  })
}

/**
 * Deletes a single inbound SKU line from an RMA (the RMA itself is kept)
 * @param rmaNumber The RMA number
 * @param lineNumber The RMA-level line number of the inbound SKU to delete
 * @returns Promise with the response
 */
export async function RmaInboundSkuDelete(rmaNumber: string, lineNumber: number) {
  const url = await GetOrderEntryConnectorUrl('RMAInboundSkuDelete')
  return ApiDelete(url, { RMANumber: rmaNumber, LineNumber: lineNumber })
}

/**
 * Deletes a single outbound SKU line from an RMA (the RMA itself is kept)
 * @param rmaNumber The RMA number
 * @param lineNumber The RMA-level line number of the outbound SKU to delete
 * @returns Promise with the response
 */
export async function RmaOutboundSkuDelete(rmaNumber: string, lineNumber: number) {
  const url = await GetOrderEntryConnectorUrl('RMAOutboundSkuDelete')
  return ApiDelete(url, { RMANumber: rmaNumber, LineNumber: lineNumber })
}

/**
 * Submits an RMA
 * @param rmaNumber The RMA number to submit
 * @returns Promise with the response
 */
export async function RmaSubmit(rmaNumber: string) {
  const url = await GetOrderEntryConnectorUrl('RmaSubmit')
  return ApiPost(url, { RMANumber: rmaNumber })
}

/**
 * Deletes an RMA
 * @param rmaNumber The RMA number to delete
 * @returns Promise with the response
 */
export async function RmaDelete(rmaNumber: string) {
  const url = await GetOrderEntryConnectorUrl('RMADelete')
  return ApiDelete(url, { RMANumber: rmaNumber })
}

/**
 * Gets an RMA
 * @param rmaNumber The RMA number to delete
 * @returns Promise with the response
 */
export async function RmaGet(rmaNumber: string) {
  const url = await GetOrderEntryConnectorUrl('RMAGet')
  return ApiGet(url, { RMANumber: rmaNumber })
}

/**
 * Creates/uploads a photo for an RMA inbound SKU
 * @param photoData Object containing rmaNumber, lineNumber, and fileName
 * @param file The file to upload
 * @returns Promise with the response
 */
export async function RmaInboundPhotoCreate(
  photoData: { rmaNumber: string; lineNumber: number; fileName: string },
  file: File
) {
  const url = await GetOrderEntryConnectorUrl('RmaInboundPhotoCreate')
  
  // API expects query parameters for metadata and file as raw request body stream
  const queryParams = {
    RMANumber: photoData.rmaNumber,
    LineNumber: photoData.lineNumber.toString(),
    InboundSkuImageName: photoData.fileName,
  }

  return ApiPostStream(url, file, queryParams)
}

/**
 * Deletes a photo from an RMA inbound SKU
 * @param rmaNumber The RMA number
 * @param lineNumber The line number
 * @param photoId The photo ID to delete
 * @returns Promise with the response
 */
export async function RmaInboundPhotoDelete(
  rmaNumber: string,
  lineNumber: number,
  photoId: string
) {
  const url = await GetOrderEntryConnectorUrl('RmaInboundPhotoDelete')
  return ApiDelete(url, {
    RMANumber: rmaNumber,
    LineNumber: lineNumber,
    PhotoId: photoId,
  })
}

/**
 * Gets the available refund/payment methods for an RMA (self-service refund-method selection)
 * @param rmaNumber The RMA number
 * @returns Promise with the list of available payment methods
 */
export async function RmaAvailablePaymentMethodGetList(rmaNumber: string) {
  const url = await GetOrderEntryConnectorUrl('RmaAvailablePaymentMethodGetList')
  return ApiGet(url, { RMANumber: rmaNumber })
}

/**
 * Selects an original order payment to refund a portion of the RMA balance to
 * @param rmaNumber The RMA number
 * @param paymentGuidRelated The paymentGuid of the original order payment being refunded
 * @param rmaEnteredRefund The refund amount to apply (negative value)
 * @returns Promise with the response
 */
export async function RmaPaymentCreate(
  rmaNumber: string,
  paymentGuidRelated: string,
  rmaEnteredRefund: number
) {
  const url = await GetOrderEntryConnectorUrl('RmaPaymentCreate')
  return ApiPost(url, {
    RMANumber: rmaNumber,
    PaymentGuidRelated: paymentGuidRelated,
    RmaEnteredRefund: rmaEnteredRefund,
  })
}

/**
 * Updates a previously selected refund amount
 * @param rmaNumber The RMA number
 * @param lineNumber The RMA payment line number to update
 * @param rmaEnteredRefund The updated refund amount (negative value)
 * @returns Promise with the response
 */
export async function RmaPaymentUpdate(
  rmaNumber: string,
  lineNumber: number,
  rmaEnteredRefund: number
) {
  const url = await GetOrderEntryConnectorUrl('RmaPaymentUpdate')
  return ApiPut(url, {
    RMANumber: rmaNumber,
    LineNumber: lineNumber,
    RmaEnteredRefund: rmaEnteredRefund,
  })
}

/**
 * Removes a previously selected refund line
 * @param rmaNumber The RMA number
 * @param lineNumber The RMA payment line number to remove
 * @returns Promise with the response
 */
export async function RmaPaymentDelete(rmaNumber: string, lineNumber: number) {
  const url = await GetOrderEntryConnectorUrl('RmaPaymentDelete')
  return ApiDelete(url, {
    RMANumber: rmaNumber,
    LineNumber: lineNumber,
  })
}
/**
 * Gets a payment gateway client token for an RMA (used to initialize the BrainTree
 * hosted-fields / PayPal flow before a new payment is created for a balance owed)
 * @param rmaPaymentTokenCreate The RMA number, gateway system keyword, and charge amount
 * @returns Promise with the response containing the gateway client token
 */
export async function RmaPaymentTokenCreate(rmaPaymentTokenCreate: {
  rmaNumber: string
  systemKeyword: string
  charge: number
}): Promise<{ item: { ClientToken?: string } }> {
  const url = await GetOrderEntryConnectorUrl('RmaPaymentTokenCreate')
  return ApiPost(url, {
    RMANumber: rmaPaymentTokenCreate.rmaNumber,
    SystemKeyword: rmaPaymentTokenCreate.systemKeyword,
    Charge: rmaPaymentTokenCreate.charge,
  })
}
